// ComboApiCatalogueCard.js
import { useEffect, useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import beveatApiResourceOwner from "../../../api/resource_owner_beveat_api.js";
import beveatApiStaging from "../../../api/staging_beveat_api.js";
import CreateCertificateDetailsModal from "../components/create_certificate_details_modal.jsx";
import { pricingPlanCurrentTabContext } from "../context/pricing_plan_context.jsx";
import { getUser } from "../../../services/local_service.js";
import { urls, commonConfigs } from "../../../../config.js";
import { DEVELOPER_PROJ_STATUS } from "../../../constants/developer_proj_status.jsx";
import { doublewriteConfig } from "../../../../config.js";
import { INVOICE_PLAN_SYSCODE, InvoicePlanSyscodeString } from "../../../constants/invoice_plan_syscode.jsx";

export default function ComboApiCatalogueCard({
  industry,
  plan,
  price,
  planId,
  benefits,
  onButtonClick,
  buttonLabel,
  isGoLive,
}) {
  const { currentTab } = useContext(pricingPlanCurrentTabContext);
  const [certificates, setCertificates] = useState([]);
  const [project, setProject] = useState();
  const [showDetails, setShowDetails] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [load, setLoad] = useState(true);
  // console.log("TEST LOG GO LIVE IN COURSE CARD:", isGoLive)

  const ssoId = getUser()?.sso_id;
  const accountId = getUser()?.account_id;
  const navigate = useNavigate();

  const getDeveloperCertificatesUrl =
    urls.resourceOwnerUrl +
    commonConfigs.apiUrls.getAllDeveloperCertificates(accountId);

  const devProjTableSyscode = currentTab.invoicePlanSyscode;

  useEffect(() => {
    async function init() {
      try {
        await getDeveloperCertificates();
        await getDeveloperProjectByPlan(planId);
        setLoad(false);
      } catch (err) {
        console.error("Initialization error:", err);
      }
    }
    init();
  }, []);

  async function getDeveloperCertificates() {
    const res = await beveatApiResourceOwner.get(getDeveloperCertificatesUrl, {
      headers: commonConfigs.resourceOwnerApiHeader,
    });
    setCertificates(res.data.result.data);
  }

  async function getDeveloperProjectByPlan(planId) {
    const url =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.getDeveloperProjectByPlan(ssoId, planId);
    const res = await beveatApiResourceOwner.get(url, {
      headers: commonConfigs.resourceOwnerApiHeader,
    });
    const projectData = res.data.result.data;
    // console.log("TEST LOG PROJECT:", projectData)
    if (Object.keys(projectData).length > 0) {
      setProject(projectData);
      if (currentTab.invoicePlanSyscode == INVOICE_PLAN_SYSCODE.Production) {
        await getDeveloperCourse(planId, projectData.developer_proj_id);
      } else {
        if (projectData.developer_proj_status === DEVELOPER_PROJ_STATUS.Active) {
          await getDeveloperCourse(planId, projectData.developer_proj_id);
        }
      }
    }
    // console.log("Project Data:", projectData);
  }

  async function getDeveloperCourse(planId, projId) {
    let isExpired = 0;
    if (currentTab.invoicePlanSyscode == INVOICE_PLAN_SYSCODE.Production) isExpired = 1;
    const url =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.getDeveloperCourses(accountId, planId, isExpired);
    // console.log("TEST LOG GO LIVE IN DEV COURSE:", isGoLive)
    try {
      const res = await beveatApiResourceOwner.get(url, {
        headers: commonConfigs.resourceOwnerApiHeader,
      });

      if (Object.keys(res.data.result.data).length !== 0) {
        setShowDetails(true);
      } else if (isGoLive) {
        setShowDetails(true);
      } else {
        await updateDeveloperProj(projId);
        await updateDoublewriteDevProj(projId);
      }
    } catch (err) {
      console.error("Error fetching developer course:", err);
    }
  }

  async function updateDeveloperProj(projId) {
    const url =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.UpdateDeveloperProject(projId, devProjTableSyscode);
    await beveatApiResourceOwner.put(
      url,
      { developer_proj_status: DEVELOPER_PROJ_STATUS.Expired },
      { headers: commonConfigs.resourceOwnerApiHeader }
    );
  }

  async function updateDoublewriteDevProj(projId) {
    const { url, headers } = doublewriteConfig(process.env.REACT_APP_ENV);
    const doublewriteUrl =
      url + commonConfigs.apiUrls.UpdateDeveloperProject(projId, devProjTableSyscode);
    await beveatApiResourceOwner.put(
      doublewriteUrl,
      { developer_proj_status: DEVELOPER_PROJ_STATUS.Expired },
      { headers }
    );
  }

  const onGetCertificateClick = () => {
    const certificate = certificates.find(
      (cert) =>
        cert.org_invoice_plan_id === planId &&
        cert.developer_proj_id === project.developer_proj_id
    );
    if (certificate) {
      navigate("/certification", {
        state: {
          planName: plan.invoice_plan,
          certificationPlanId: planId,
          environment: InvoicePlanSyscodeString[currentTab.invoicePlanSyscode],
          devProjId: project.developer_proj_id,
        },
      });
    } else {
      setOpen(true);
    }
  };

  if (!load) {
    return (
      <div className="bg-white shadow-lg rounded-lg w-full mx-auto h-[26rem] flex flex-col justify-between" style={{ maxWidth: "20rem" }}>
        <div className="p-6 text-black">
          <h2 className="text-gray-700 font-bold mb-2 text-[25px]">
            {currentTab.invoicePlanSyscode === 3 && currentTab.invoiceSyscode === 3 ? "Events" : "Industry"}
          </h2>
          <div className="bg-blue-400 text-white font-bold text-lg py-1 rounded-full mb-2">{industry}</div>
          <p className="text-3xl font-bold text-gray-800 mb-1 line-through text-black">${price}</p>
          <p className="text-red-600 text-sm">with Course Code</p>
          <ul className="text-left" style={{ listStyleType: "circle", paddingLeft: "20px", marginTop: "24px" }}>
            {benefits.map((benefit, index) =>
              typeof benefit === "string" ? (
                <li key={index} className="mb-2">{benefit}</li>
              ) : (
                <li key={index} className="mb-2">
                  {benefit.title}
                  <ul style={{ paddingLeft: "20px", listStyleType: "circle" }}>
                    {benefit.children.map((child, i) => (
                      <li key={i} className="mb-2">{child}</li>
                    ))}
                  </ul>
                </li>
              )
            )}
          </ul>
        </div>
        <div>
          {showDetails ? (
            currentTab.invoicePlanSyscode == INVOICE_PLAN_SYSCODE.Production ? (
              <div className="flex border-t border-white">
                <Link
                  to={`/projects`}
                  className="flex-1 bg-blue-400 text-white py-3 font-semibold hover:bg-blue-600 text-center"
                  state={{
                    developerSsoId: ssoId,
                    developerProjId: project.developer_proj_id,
                    invoicePlanSyscode: currentTab.invoicePlanSyscode
                  }}
                >
                  View Projects
                </Link>
                <div className="w-[1px] bg-white"></div>
                <button
                  onClick={onButtonClick}
                  className="flex-1 bg-blue-400 text-white py-3 font-semibold hover:bg-blue-600"
                >
                  Apply Voucher Code
                </button>
              </div>
            ) : (
              <div className="flex border-t border-white">
                <Link
                  to={`/projects/details`}
                  className="flex-1 bg-blue-400 text-white py-3 font-semibold hover:bg-blue-600 text-center"
                  state={{
                    developerSsoId: ssoId,
                    developerProjId: project.developer_proj_id
                  }}
                >
                  View Project Details
                </Link>
                <div className="w-[1px] bg-white"></div>
                <button
                  onClick={onGetCertificateClick}
                  className="flex-1 bg-blue-400 text-white py-3 font-semibold hover:bg-blue-600"
                >
                  Get Course Certificate
                </button>
              </div>
            )
          ) : (
            <button
              onClick={onButtonClick}
              className="bg-blue-400 hover:bg-blue-600 text-white py-3 px-4 w-full font-semibold"
            >
              {buttonLabel || "Apply Course Code"}
            </button>
          )}
        </div>
        {isOpen && (
          <CreateCertificateDetailsModal
            setOpenModal={setOpen}
            planName={plan.invoice_plan_category}
            planId={planId}
            devProjId={project.developer_proj_id}
            environment={InvoicePlanSyscodeString[currentTab.invoicePlanSyscode]}
          />
        )}
      </div>
    );
  }

  return null;
}
